import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Newspaper, Zap, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function formatWeek(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

const CATEGORY_LABEL = {
  news: "Actualités", blog: "Blog", caf_dates: "CAF",
  crous_dates: "CROUS", housing: "Logement", scam_alerts: "Anti-arnaques",
  app_updates: "Nouveautés app", maintenance: "Maintenance", premium_promo: "Premium",
};

export default function DigestStatsSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/digests/stats");
      setStats(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur chargement stats digest");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async (force) => {
    if (force && !window.confirm("Forcer l'envoi du digest cette semaine ? (bypass idempotence — peut re-envoyer si déjà reçu)")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/digests/run-now${force ? "?force=true" : ""}`);
      toast.success(`✅ ${data.sent} digests envoyés · ${data.skipped_empty} sans nouveauté · ${data.skipped_already} déjà envoyés`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8"
      data-testid="digest-stats-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-fuchsia-500 font-semibold">
            Digest hebdomadaire
          </p>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1 flex items-center gap-2">
            <Newspaper size={20} /> Résumés dimanche 9h Paris
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Chaque utilisateur reçoit un résumé personnalisé selon ses catégories opt-in. Envoyé automatiquement, idempotent par semaine.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={() => runNow(false)} disabled={busy}
            data-testid="digest-run-now-btn"
          >
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Zap size={12} className="mr-1" />}
            Lancer maintenant
          </Button>
          <Button
            variant="outline" size="sm" onClick={() => runNow(true)} disabled={busy}
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            data-testid="digest-force-run-btn"
          >
            Forcer (bypass idempotence)
          </Button>
        </div>
      </div>

      {loading && (
        <p className="mt-6 text-sm text-slate-400 flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Chargement…
        </p>
      )}

      {!loading && stats && (
        <>
          {/* Overall KPIs */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Envoyés (8 sem.)</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{stats.overall.sent}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Ouverts</p>
              <p className="font-display text-2xl font-bold text-emerald-800 mt-1">{stats.overall.opened}</p>
              <p className="text-xs text-emerald-600 mt-0.5">{stats.overall.open_rate}%</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Cliqués</p>
              <p className="font-display text-2xl font-bold text-amber-800 mt-1">{stats.overall.clicked}</p>
              <p className="text-xs text-amber-600 mt-0.5">{stats.overall.click_rate}% CTR</p>
            </div>
            <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4">
              <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Provider</p>
              <p className="font-mono text-sm font-semibold text-indigo-900 mt-1">{stats.provider}</p>
              <p className="text-[10px] text-indigo-500 mt-0.5">asyncio scheduler</p>
            </div>
          </div>

          {/* Weekly breakdown */}
          {stats.weekly.length > 0 && (
            <div className="mt-6" data-testid="digest-weekly-table">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Semaines récentes
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs text-slate-500">
                      <th className="py-2 pr-4 font-medium">Semaine du</th>
                      <th className="py-2 pr-4 font-medium text-right">Digests</th>
                      <th className="py-2 font-medium text-right">Items totaux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.weekly.map((w) => (
                      <tr key={w.week_start} className="border-b border-slate-100">
                        <td className="py-2 pr-4 font-mono text-xs">{formatWeek(w.week_start)}</td>
                        <td className="py-2 pr-4 text-right font-semibold text-slate-900">{w.digests}</td>
                        <td className="py-2 text-right text-slate-700">{w.total_items}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top categories */}
          {stats.top_categories.length > 0 && (
            <div className="mt-6" data-testid="digest-top-categories">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
                <TrendingUp size={12} /> Catégories les plus présentes
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {stats.top_categories.map((c) => (
                  <div key={c.category} className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                    <span className="text-sm text-slate-700">
                      {CATEGORY_LABEL[c.category] || c.category}
                    </span>
                    <span className="text-sm font-semibold text-slate-900">{c.n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
